// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCsvExport } from '@hooks/other/useCsvExport';
import { useDialogControl } from '@hooks/other/useDialogControl';
import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Burn } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Model/Burn';
import { Batch } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Types';
import CopyableText from '../../../components/CopyableText';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { StateCell, Status } from '../../../components/StateCell';
import { DataTable } from '../../../components/Table/DataTable';
import { useExecutedBurns2Step } from '../../../hooks/queries/registry/burn/useExecutedBurns2Step';
import { useRejectedBurns2Step } from '../../../hooks/queries/registry/burn/useRejectedBurns2Step';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import {
  InstrumentsById,
  useInstrumentConfigurationsById,
} from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import { fmt, formatTimestamp, parseTimestampToDate } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';
import { RegistrarBurnDialog } from './RegistrarBurnDialog';

type Row = {
  contractId: string;
  state: string;
  registrar: string;
  payload: Burn;
  reason?: string;
};

const createHeaders: (instrumentsById: InstrumentsById) => GridColDef<Row>[] = (
  instrumentsById,
) => [
  {
    field: 'state',
    headerName: 'State',
    renderCell: (params) => (
      <StateCell state={params.row.state as Status} reason={params.row.reason} />
    ),
    flex: 1,
    filterable: true,
    valueGetter: (params) => params.row.state,
  },
  {
    field: 'instrumentId',
    headerName: 'Instrument Id',
    valueGetter: (params) => params.row.payload.instrumentId.id,
    renderCell: (params) => <CopyableText value={params.row.payload.instrumentId.id} />,
    flex: 1,
    filterable: true,
  },
  {
    field: 'tokenName',
    headerName: 'Token Name',
    valueGetter: (params) =>
      instrumentsById[params.row.payload.instrumentId.id]?.payload.tokenName || '',
    flex: 1,
    filterable: true,
  },
  {
    field: 'tokenSymbol',
    headerName: 'Token Symbol',
    valueGetter: (params) =>
      instrumentsById[params.row.payload.instrumentId.id]?.payload.tokenSymbol || '',
    flex: 1,
    filterable: true,
  },
  {
    field: 'cusip',
    headerName: 'CUSIP',
    valueGetter: (params) =>
      instrumentsById[params.row.payload.instrumentId.id]?.payload.cusip || '',
    flex: 1,
    filterable: true,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    renderCell: (params) => fmt(params.row.payload.amount, 2),
    valueGetter: (params) => Number(params.row.payload.amount),
    flex: 1,
    filterable: true,
  },
  {
    field: 'registrar',
    headerName: 'Registrar',
    renderCell: (params) => <PartyDisplay party={params.row.registrar} />,
    flex: 1,
    filterable: true,
  },
  {
    field: 'holder',
    headerName: 'Holder',
    renderCell: (params) => <PartyDisplay party={params.row.payload.holder} />,
    flex: 1,
    filterable: true,
    valueGetter: (params) => params.row.payload.holder,
  },
  {
    field: 'reference',
    headerName: 'Reference',
    valueGetter: (params) => params.row.payload.reference,
    flex: 1,
    filterable: true,
  },
  {
    field: 'requestedAt',
    headerName: 'Requested At',
    type: 'dateTime',
    valueGetter: (params) => parseTimestampToDate(params.row.payload.requestedAt),
    renderCell: (params) => formatTimestamp(params.row.payload.requestedAt),
    flex: 1,
    minWidth: 180,
  },
  {
    field: 'executeBefore',
    headerName: 'Execute Before',
    type: 'dateTime',
    valueGetter: (params) => parseTimestampToDate(params.row.payload.executeBefore),
    renderCell: (params) => formatTimestamp(params.row.payload.executeBefore),
    flex: 1,
    minWidth: 180,
  },
];

interface BurnsProps {
  disableVirtualization?: boolean;
}

export const Burns: React.FC<BurnsProps> = ({ disableVirtualization }) => {
  const registrarService = useRegistrarService();

  const rejectedBurns2Step = useRejectedBurns2Step();
  const executedBurns2Step = useExecutedBurns2Step();
  const instrumentsById = useInstrumentConfigurationsById();

  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();

  const isLoading =
    registrarService.isLoading || rejectedBurns2Step.isLoading || executedBurns2Step.isLoading;

  const columns = useMemo(() => createHeaders(instrumentsById), [instrumentsById]);

  const rows = useMemo(() => {
    return rejectedBurns2Step.data
      .map<Row>((r) => ({
        contractId: r.contractId,
        state: 'Rejected2Step',
        reason: r.payload.reason,
        registrar: r.payload.burn.instrumentId.admin,
        payload: {
          ...r.payload.burn,
          operator: r.payload.operator,
          instrumentId: {
            ...r.payload.burn.instrumentId,
            scheme: '',
            source: r.payload.burn.instrumentId.admin,
          },
          batch: {} as Batch,
        },
      }))
      .concat(
        executedBurns2Step.data.map((r) => ({
          contractId: r.contractId,
          state: 'Executed2Step',
          registrar: r.payload.burn.instrumentId.admin,
          payload: {
            ...r.payload.burn,
            operator: r.payload.operator,
            instrumentId: {
              ...r.payload.burn.instrumentId,
              scheme: '',
              source: r.payload.burn.instrumentId.admin,
            },
            batch: {} as Batch,
          },
        })),
      );
  }, [rejectedBurns2Step.data, executedBurns2Step.data]);

  const isRegistrar = !!registrarService.data;

  const { handleExport, isExportDisabled } = useCsvExport(columns, rows, {
    filename: 'burns',
  });

  const buttons = [
    {
      name: 'Export CSV',
      onClick: handleExport,
      disabled: isExportDisabled,
    },
    ...(isRegistrar
      ? [
          {
            name: 'Registrar Burn',
            onClick: () => setDialogName('registrar-burn'),
          },
        ]
      : []),
  ];

  if (isLoading) return <Loading />;

  return (
    <PageWrapper buttons={buttons} pageName="Burns">
      {dialogName === 'registrar-burn' && <RegistrarBurnDialog open onClose={onCloseDialog} />}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(a) => a.contractId}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
